import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, ChevronRight } from 'lucide-react'
import { Alert, Popover } from 'antd'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type Notification,
} from '@/hooks/use-notifications'
import { usePendingTasks, type PendingTaskItem } from '@/hooks/use-pending-tasks'

/** 极简相对时间：刚刚 / N分钟前 / N小时前 / N天前 / 日期 */
function timeAgo(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso).getTime()
  if (Number.isNaN(d)) return ''
  const diff = Date.now() - d
  const m = Math.floor(diff / 60000)
  if (m < 1) return '刚刚'
  if (m < 60) return `${m}分钟前`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}小时前`
  const day = Math.floor(h / 24)
  if (day < 7) return `${day}天前`
  return new Date(iso).toLocaleDateString('zh-CN')
}

/** 小节标题 */
function SectionTitle({
  title,
  count,
  extra,
}: {
  title: string
  count?: number
  extra?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between px-1 pb-1.5">
      <span className="text-xs font-semibold tracking-wide text-slate-500">
        {title}
        {count ? (
          <span className="ml-1 font-mono text-slate-400">
            · {count > 99 ? '99+' : count}
          </span>
        ) : (
          ''
        )}
      </span>
      {extra}
    </div>
  )
}

/** 待办任务栏：按角色的业务链路待办 + 审批流待办,点击进对应处理页 */
function PendingTasksSection({
  onNavigate,
  items,
  total,
  error,
}: {
  onNavigate: (to: string) => void
  error?: Error | null
  items: PendingTaskItem[]
  total: number
}) {
  return (
    <div>
      <SectionTitle title="待办任务" count={total} />
      {error ? <Alert type="error" message="任务数量加载失败，请刷新重试" /> : items.length === 0 ? (
        <div className="py-3 text-center text-xs text-slate-400">暂无待办任务</div>
      ) : (
        <div className="-mx-1">
          {items.map((t) => {
            const Icon = t.icon
            return (
              <button
                key={t.key}
                onClick={() => onNavigate(t.href)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-slate-50"
              >
                <span
                  className={cn(
                    'flex h-6 w-6 flex-none items-center justify-center rounded-md',
                    t.approval ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'
                  )}
                >
                  <Icon size={14} />
                </span>
                <span className="flex-1 truncate text-sm text-slate-700">{t.label}</span>
                <span
                  className={cn(
                    'font-mono text-sm font-semibold tabular-nums',
                    t.approval ? 'text-amber-600' : 'text-blue-600'
                  )}
                >
                  {t.count}
                </span>
                <ChevronRight className="h-3.5 w-3.5 flex-none text-slate-300" />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** 通知消息栏 */
function MessagesSection({ onNavigate }: { onNavigate: (to: string) => void }) {
  const { data, isLoading } = useNotifications({ limit: 6 })
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  const items: Notification[] = data?.data || []
  const unread = data?.statistics?.unread || 0

  const handleClick = (n: Notification) => {
    if (!n.isRead) markRead.mutate(n.id)
    onNavigate(
      n.relatedOrderId
        ? `/dashboard/orders/${n.relatedOrderId}`
        : '/dashboard/notifications'
    )
  }

  return (
    <div>
      <SectionTitle
        title="通知消息"
        count={unread}
        extra={
          unread > 0 ? (
            <button
              onClick={() => markAll.mutate()}
              className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              <Check className="h-3 w-3" /> 全部已读
            </button>
          ) : null
        }
      />
      <div className="-mx-1 max-h-72 overflow-y-auto">
        {isLoading && (
          <div className="py-6 text-center text-xs text-slate-400">加载中...</div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="py-3 text-center text-xs text-slate-400">暂无通知消息</div>
        )}
        {items.map((n) => (
          <button
            key={n.id}
            onClick={() => handleClick(n)}
            className="flex w-full gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-slate-50"
          >
            <span
              className={cn(
                'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                n.isRead ? 'bg-transparent' : 'bg-red-500'
              )}
            />
            <span className="min-w-0 flex-1">
              <span className="flex items-center justify-between gap-2">
                <span
                  className={cn(
                    'truncate text-sm',
                    n.isRead ? 'text-slate-600' : 'font-medium text-slate-900'
                  )}
                >
                  {n.title}
                </span>
                <span className="shrink-0 text-[11px] text-slate-400">
                  {timeAgo(n.createdAt)}
                </span>
              </span>
              <span className="block truncate text-xs text-slate-500">{n.content}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function NotificationPanel({
  onNavigate,
  pendingItems,
  pendingTotal,
  pendingError,
}: {
  onNavigate: (to: string) => void
  pendingItems: PendingTaskItem[]
  pendingTotal: number
  pendingError?: Error | null
}) {
  return (
    <div className="w-80">
      <PendingTasksSection error={pendingError}
        onNavigate={onNavigate}
        items={pendingItems}
        total={pendingTotal}
      />
      <div className="my-2 border-t border-slate-100" />
      <MessagesSection onNavigate={onNavigate} />
      <div className="mt-1 border-t border-slate-100 pt-2 text-center">
        <button
          onClick={() => onNavigate('/dashboard/notifications')}
          className="text-xs text-slate-500 transition-colors hover:text-slate-700"
        >
          查看全部
        </button>
      </div>
    </div>
  )
}

/**
 * 顶部通知铃铛：hover 浮层分「待办任务」+「通知消息」两栏;
 * 点铃铛进通知中心。
 */
export function NotificationBell({ unreadCount }: { unreadCount: number }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { items: pendingItems, total: pendingTotal, error: pendingError } = usePendingTasks()
  const attentionCount = Math.max(0, pendingTotal) + Math.max(0, unreadCount)

  const go = (to: string) => {
    setOpen(false)
    navigate(to)
  }

  return (
    <Popover
      trigger="hover"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      content={
        <NotificationPanel
          onNavigate={go}
          pendingItems={pendingItems}
          pendingTotal={pendingTotal}
          pendingError={pendingError}
        />
      }
    >
      <Button
        variant="ghost"
        size="icon"
        className="relative rounded-lg transition-colors hover:bg-slate-100"
        title={`通知中心：${pendingTotal} 项待办，${unreadCount} 条未读消息`}
        onClick={() => go('/dashboard/notifications')}
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {attentionCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center px-1 text-xs shadow-lg"
          >
            {attentionCount > 99 ? '99+' : attentionCount}
          </Badge>
        )}
      </Button>
    </Popover>
  )
}
